import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Save,
  Loader2,
  Plus,
  Trash2,
  Upload,
  Pencil,
  X,
  Check,
  Monitor,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import { useAuth } from "@/contexts/AuthContext";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageEditorTab() {
  const [content, setContent] = useState("");
  const [label, setLabel] = useState("");
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingLabel, setSavingLabel] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/epic-practice/settings`, { withCredentials: true })
      .then(({ data }) => {
        setContent(data.content || "");
        setPublished(data.published || false);
        setLabel(data.label || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (pub) => {
    setSaving(true);
    try {
      await axios.put(`${API}/epic-practice/settings`, { content, published: pub }, { withCredentials: true });
      setPublished(pub);
      toast.success(pub ? "Published!" : "Saved as draft");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLabel = async () => {
    setSavingLabel(true);
    try {
      await axios.put(`${API}/epic-practice/settings`, { label: label.trim() }, { withCredentials: true });
      toast.success("Display label saved — refresh to see it in the sidebar");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingLabel(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="epic-page-editor">
      {/* Display label — org-level sandbox name (e.g., "Cerner Training", "EHR Sandbox") */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-5">
        <Label className="text-[#A1A9B8] font-body text-sm mb-2 block">Display Label</Label>
        <p className="text-xs text-[#7A8699] mb-3">
          Optional custom name for this sandbox feature. Shown in the sidebar and page header. Leave blank to use the default "Sandbox".
        </p>
        <div className="flex gap-3">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Cerner Training Sandbox"
            maxLength={60}
            className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#5A6372]"
            data-testid="epic-label-input"
          />
          <Button
            onClick={handleSaveLabel}
            disabled={savingLabel}
            data-testid="epic-label-save-btn"
            className="h-10 px-4 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            {savingLabel ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Label"}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-[#A1A9B8] font-body text-sm">Instructions Content</Label>
          <Badge
            className={
              published
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                : "bg-amber-500/15 text-amber-400 border-amber-500/20"
            }
          >
            {published ? "Published" : "Draft"}
          </Badge>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => handleSave(false)}
            disabled={saving}
            variant="outline"
            data-testid="epic-save-draft-btn"
            className="h-9 px-4 font-body font-medium border-white/10 text-[#A1A9B8] hover:text-white hover:bg-white/5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save Draft
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving}
            data-testid="epic-publish-btn"
            className="h-9 px-4 font-body font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
            Publish
          </Button>
        </div>
      </div>
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-6">
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder="Write instructions for learners..."
          testId="epic-instructions-editor"
        />
      </div>
    </div>
  );
}

function CredentialRow({ cred, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ login_id: cred.login_id, password: cred.password, department: cred.department || "", patient_name: cred.patient_name || "", patient_mrn: cred.patient_mrn });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/epic-practice/credentials/${cred.id}`, form, { withCredentials: true });
      onUpdate(cred.id, form);
      setEditing(false);
      toast.success("Updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <tr className="border-b border-white/5" data-testid={`cred-row-${cred.id}`}>
        <td className="py-2 px-3">
          <Input value={form.login_id} onChange={(e) => setForm({ ...form, login_id: e.target.value })} className="bg-[#0A0C10] border-white/10 text-white h-8 text-sm" data-testid={`cred-edit-login-${cred.id}`} />
        </td>
        <td className="py-2 px-3">
          <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0C10] border-white/10 text-white h-8 text-sm" data-testid={`cred-edit-password-${cred.id}`} />
        </td>
        <td className="py-2 px-3">
          <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-[#0A0C10] border-white/10 text-white h-8 text-sm" data-testid={`cred-edit-department-${cred.id}`} />
        </td>
        <td className="py-2 px-3">
          <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="bg-[#0A0C10] border-white/10 text-white h-8 text-sm" data-testid={`cred-edit-patient-name-${cred.id}`} />
        </td>
        <td className="py-2 px-3">
          <Input value={form.patient_mrn} onChange={(e) => setForm({ ...form, patient_mrn: e.target.value })} className="bg-[#0A0C10] border-white/10 text-white h-8 text-sm" data-testid={`cred-edit-mrn-${cred.id}`} />
        </td>
        <td className="py-2 px-3">
          <div className="flex gap-1">
            <button onClick={handleSave} disabled={saving} className="h-7 w-7 rounded-md flex items-center justify-center text-emerald-400 hover:bg-emerald-500/10" data-testid={`cred-save-${cred.id}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </button>
            <button onClick={() => setEditing(false)} className="h-7 w-7 rounded-md flex items-center justify-center text-[#6E7787] hover:bg-white/5">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-white/5 group" data-testid={`cred-row-${cred.id}`}>
      <td className="py-2.5 px-3 text-sm text-white font-body font-mono">
        <div className="flex items-center gap-2">
          {cred.login_id}
        </div>
      </td>
      <td className="py-2.5 px-3 text-sm text-white font-body font-mono">{cred.password}</td>
      <td className="py-2.5 px-3 text-sm text-white font-body">{cred.department}</td>
      <td className="py-2.5 px-3 text-sm text-white font-body">{cred.patient_name}</td>
      <td className="py-2.5 px-3 text-sm text-white font-body font-mono">{cred.patient_mrn}</td>
      <td className="py-2.5 px-3">
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => setEditing(true)} className="h-7 w-7 rounded-md flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid={`cred-edit-${cred.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onDelete(cred.id)} className="h-7 w-7 rounded-md flex items-center justify-center text-red-400 hover:bg-red-500/10" data-testid={`cred-delete-${cred.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function RoleSection({ role, onRename, onDelete }) {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newCred, setNewCred] = useState({ login_id: "", password: "", department: "", patient_name: "", patient_mrn: "" });
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(role.name);
  const fileRef = useRef(null);

  const fetchCreds = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/epic-practice/roles/${role.id}/credentials`, { withCredentials: true })
      .then(({ data }) => setCreds(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [role.id]);

  useEffect(() => { fetchCreds(); }, [fetchCreds]);

  const handleAdd = async () => {
    if (!newCred.login_id.trim()) return;
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/epic-practice/roles/${role.id}/credentials`, newCred, { withCredentials: true });
      setCreds((p) => [...p, data]);
      setNewCred({ login_id: "", password: "", department: "", patient_name: "", patient_mrn: "" });
      setShowAdd(false);
      toast.success("Credential added");
    } catch {
      toast.error("Add failed");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (credId) => {
    try {
      await axios.delete(`${API}/epic-practice/credentials/${credId}`, { withCredentials: true });
      setCreds((p) => p.filter((c) => c.id !== credId));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleUpdate = (credId, updated) => {
    setCreds((p) => p.map((c) => (c.id === credId ? { ...c, ...updated } : c)));
  };

  const handleRename = async () => {
    if (!newName.trim()) return;
    try {
      await axios.put(`${API}/epic-practice/roles/${role.id}`, { name: newName }, { withCredentials: true });
      onRename(role.id, newName);
      setRenaming(false);
      toast.success("Renamed");
    } catch {
      toast.error("Rename failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.trim().split("\n");
    if (lines.length < 2) { toast.error("CSV must have a header row + data"); return; }
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    const loginIdx = header.findIndex((h) => h.includes("login") || h === "id");
    const passIdx = header.findIndex((h) => h.includes("password") || h.includes("pass"));
    const deptIdx = header.findIndex((h) => h.includes("department") || h.includes("dept"));
    const nameIdx = header.findIndex((h) => h.includes("patient_name") || h.includes("patient name") || h === "name");
    const mrnIdx = header.findIndex((h) => h.includes("mrn") || (h.includes("patient") && !h.includes("name")));
    if (loginIdx === -1) { toast.error("CSV must contain a 'Login ID' or 'ID' column"); return; }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map((c) => c.trim());
      if (!cols[loginIdx]) continue;
      rows.push({
        login_id: cols[loginIdx] || "",
        password: passIdx >= 0 ? cols[passIdx] || "" : "",
        department: deptIdx >= 0 ? cols[deptIdx] || "" : "",
        patient_name: nameIdx >= 0 ? cols[nameIdx] || "" : "",
        patient_mrn: mrnIdx >= 0 ? cols[mrnIdx] || "" : "",
      });
    }
    if (rows.length === 0) { toast.error("No valid rows found"); return; }
    try {
      const { data } = await axios.post(`${API}/epic-practice/roles/${role.id}/credentials/import`, { rows }, { withCredentials: true });
      toast.success(`Imported ${data.imported} credentials`);
      fetchCreds();
    } catch {
      toast.error("Import failed");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid={`role-section-${role.id}`}>
      {/* Role header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
        {renaming ? (
          <div className="flex items-center gap-2 flex-1 mr-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
              autoFocus
              className="bg-[#0A0C10] border-white/10 text-white h-8 text-sm max-w-xs"
              data-testid={`role-rename-input-${role.id}`}
            />
            <button onClick={handleRename} className="h-7 w-7 rounded-md flex items-center justify-center text-emerald-400 hover:bg-emerald-500/10" data-testid={`role-rename-save-${role.id}`}>
              <Check className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => { setRenaming(false); setNewName(role.name); }} className="h-7 w-7 rounded-md flex items-center justify-center text-[#6E7787] hover:bg-white/5">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity"
            data-testid={`role-toggle-${role.id}`}
          >
            {expanded ? <ChevronDown className="h-4 w-4 text-[#6E7787]" /> : <ChevronRight className="h-4 w-4 text-[#6E7787]" />}
            <h3 className="font-body text-sm font-bold text-white">{role.name}</h3>
            <Badge className="bg-white/5 text-[#6E7787] border-white/10 text-xs">{creds.length} credentials</Badge>
          </button>
        )}
        <div className="flex items-center gap-1">
          <input ref={fileRef} type="file" accept=".csv" onChange={handleImport} className="hidden" id={`import-${role.id}`} data-testid={`role-import-input-${role.id}`} />
          <label
            htmlFor={`import-${role.id}`}
            className="h-7 px-2.5 rounded-md flex items-center gap-1.5 text-xs text-[#A1A9B8] font-body cursor-pointer hover:text-white hover:bg-white/5 transition-colors"
            data-testid={`role-import-btn-${role.id}`}
          >
            <Upload className="h-3 w-3" />
            Import CSV
          </label>
          <button
            onClick={() => { setExpanded(true); setShowAdd(true); }}
            className="h-7 px-2.5 rounded-md flex items-center gap-1.5 text-xs font-body text-white hover:bg-white/5 transition-colors"
            style={{ color: "var(--primary-color, #4A90D9)" }}
            data-testid={`role-add-cred-btn-${role.id}`}
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
          {!renaming && (
            <button onClick={() => setRenaming(true)} className="h-7 w-7 rounded-md flex items-center justify-center text-[#6E7787] hover:text-white hover:bg-white/5" data-testid={`role-rename-btn-${role.id}`}>
              <Pencil className="h-3 w-3" />
            </button>
          )}
          <button onClick={() => onDelete(role.id)} className="h-7 w-7 rounded-md flex items-center justify-center text-red-400 hover:bg-red-500/10" data-testid={`role-delete-btn-${role.id}`}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Credentials table — collapsible */}
      {expanded && (
        loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-[#6E7787]" /></div>
        ) : creds.length === 0 && !showAdd ? (
          <div className="px-5 py-6 text-center">
            <p className="text-sm text-[#6E7787] font-body">No credentials yet. Add manually or import a CSV.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-xs text-[#6E7787] font-body font-medium px-3 py-2">Login ID</th>
                  <th className="text-left text-xs text-[#6E7787] font-body font-medium px-3 py-2">Password</th>
                  <th className="text-left text-xs text-[#6E7787] font-body font-medium px-3 py-2">Department</th>
                  <th className="text-left text-xs text-[#6E7787] font-body font-medium px-3 py-2">Patient Name</th>
                  <th className="text-left text-xs text-[#6E7787] font-body font-medium px-3 py-2">Patient MRN</th>
                  <th className="w-20 px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {creds.map((c) => (
                  <CredentialRow key={c.id} cred={c} onDelete={handleDelete} onUpdate={handleUpdate} />
                ))}
                {showAdd && (
                  <tr className="border-b border-white/5" data-testid="cred-add-row">
                    <td className="py-2 px-3">
                      <Input value={newCred.login_id} onChange={(e) => setNewCred({ ...newCred, login_id: e.target.value })} placeholder="Login ID" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-8 text-sm" data-testid="new-cred-login" />
                    </td>
                    <td className="py-2 px-3">
                      <Input value={newCred.password} onChange={(e) => setNewCred({ ...newCred, password: e.target.value })} placeholder="Password" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-8 text-sm" data-testid="new-cred-password" />
                    </td>
                    <td className="py-2 px-3">
                      <Input value={newCred.department} onChange={(e) => setNewCred({ ...newCred, department: e.target.value })} placeholder="Department" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-8 text-sm" data-testid="new-cred-department" />
                    </td>
                    <td className="py-2 px-3">
                      <Input value={newCred.patient_name} onChange={(e) => setNewCred({ ...newCred, patient_name: e.target.value })} placeholder="Patient Name" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-8 text-sm" data-testid="new-cred-patient-name" />
                    </td>
                    <td className="py-2 px-3">
                      <Input value={newCred.patient_mrn} onChange={(e) => setNewCred({ ...newCred, patient_mrn: e.target.value })} placeholder="Patient MRN" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-8 text-sm" data-testid="new-cred-mrn" />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1">
                        <button onClick={handleAdd} disabled={adding} className="h-7 w-7 rounded-md flex items-center justify-center text-emerald-400 hover:bg-emerald-500/10" data-testid="new-cred-save">
                          {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={() => { setShowAdd(false); setNewCred({ login_id: "", password: "", department: "", patient_name: "", patient_mrn: "" }); }} className="h-7 w-7 rounded-md flex items-center justify-center text-[#6E7787] hover:bg-white/5">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function CredentialPoolTab() {
  const [roles, setRoles] = useState([]);
  const [deleteRoleTarget, setDeleteRoleTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newRoleName, setNewRoleName] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchRoles = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/epic-practice/roles`, { withCredentials: true })
      .then(({ data }) => setRoles(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleCreate = async () => {
    if (!newRoleName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/epic-practice/roles`, { name: newRoleName }, { withCredentials: true });
      setRoles((p) => [...p, data]);
      setNewRoleName("");
      toast.success("Role created");
    } catch {
      toast.error("Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = async () => {
    const role = deleteRoleTarget;
    if (!role) return;
    try {
      await axios.delete(`${API}/epic-practice/roles/${role.id}`, { withCredentials: true });
      setRoles((p) => p.filter((r) => r.id !== role.id));
      toast.success("Role deleted");
    } catch {
      toast.error("Delete failed");
      throw new Error("delete failed");
    }
  };

  const handleRename = (roleId, newName) => {
    setRoles((p) => p.map((r) => (r.id === roleId ? { ...r, name: newName } : r)));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="epic-credential-pool">
      {/* Add Role */}
      <div className="flex gap-3">
        <Input
          value={newRoleName}
          onChange={(e) => setNewRoleName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          placeholder="New role name (e.g. RN, MD, Pharmacist)"
          data-testid="new-role-name-input"
          className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10 max-w-sm"
        />
        <Button
          onClick={handleCreate}
          disabled={creating || !newRoleName.trim()}
          data-testid="create-role-btn"
          className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
          Create Role
        </Button>
      </div>

      {/* Roles */}
      {roles.length === 0 ? (
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-12 text-center">
          <Monitor className="h-10 w-10 text-[#6E7787] mx-auto mb-3 opacity-50" />
          <p className="text-sm text-[#6E7787] font-body">No roles created yet. Create a role to start adding credentials.</p>
        </div>
      ) : (
        roles.map((role) => (
          <RoleSection key={role.id} role={role} onRename={handleRename} onDelete={(rid) => setDeleteRoleTarget(roles.find((r) => r.id === rid) || { id: rid, name: "this role" })} />
        ))
      )}

      <DeleteConfirmDialog
        open={!!deleteRoleTarget}
        onOpenChange={(open) => { if (!open) setDeleteRoleTarget(null); }}
        tier={1}
        entityName={deleteRoleTarget?.name}
        description="Permanently delete this role and all its credentials?"
        onConfirm={handleDeleteRole}
      />
    </div>
  );
}

export default function EpicPractice() {
  const [tab, setTab] = useState("editor");
  const { user } = useAuth();
  const displayLabel = (user?.epic_practice_label || "").trim() || "Sandbox";

  const tabs = [
    { id: "editor", label: "Page Editor" },
    { id: "pool", label: "Credential Pool" },
  ];

  return (
    <div className="max-w-4xl space-y-6" data-testid="epic-practice-admin">
      <h2 className="font-heading text-2xl font-bold text-white tracking-tight">{displayLabel}</h2>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#11141A] border border-white/10 rounded-xl p-1 w-fit" data-testid="epic-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`epic-tab-${t.id}`}
            className={`px-4 py-2 rounded-lg text-sm font-body font-medium transition-all ${
              tab === t.id
                ? "bg-white/10 text-white"
                : "text-[#6E7787] hover:text-[#A1A9B8]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "editor" && <PageEditorTab />}
      {tab === "pool" && <CredentialPoolTab />}
    </div>
  );
}
